import {
  makeStyles,
  IconButton,
  Tooltip,
  CircularProgress,
  withStyles,
  Link as MaterialLink,
} from '@material-ui/core';
import DOMPurify from 'dompurify';
import { SyntheticEvent, useEffect } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { COLORS } from '../../theme/variables';
import { createMarkup } from '../../utils/functions';
import { Text } from '../common';
import NoIcon from '../common/no-icon';

interface PopupIconTooltipProps {
  content?: string | null;
  errorText?: string;
  emptyText?: string;
  isLoading?: boolean;
  isError?: boolean;
  onHover?: () => void;
}

interface PopupIconProps extends PopupIconTooltipProps {
  icon: JSX.Element;
  to?: LinkProps['to'];
  testId?: string;
  isNoIcon?: boolean;
  enterTouchDelay?: number;
}

const STooltip = withStyles(() => ({
  tooltip: {
    width: 300,
    padding: 16,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    boxShadow:
      '0px 2px 4px rgba(34, 91, 187, 0.2), 0px 4px 8px rgba(51, 126, 255, 0.12)',
  },
}))(Tooltip);

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
  },
  link: {
    '&:hover': {
      borderBottom: 'none',
    },
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bodyText: {
    color: COLORS.COLOR_TEXT_BASE,
    display: '-webkit-box',
    WebkitLineClamp: 12,
    WebkitBoxOrient: 'vertical',
    textAlign: 'left',
    wordBreak: 'break-word',
    overflow: 'hidden',

    '& ul, ol, li, b, i, u, h1, h2, pre': {
      margin: 'revert',
      padding: 'revert',
      fontWeight: 'revert',
      fontStyle: 'revert',
      listStyle: 'revert',
      border: 'revert',
      fontSize: 'revert',
      font: 'revert',
      verticalAlign: 'revert',
      lineHeight: 'normal !important',
    },
  },
}));

function PopupIconooltip({
  content,
  errorText = 'Something went wrong... Please try again later.',
  emptyText = 'No data available',
  isLoading,
  isError,
  onHover,
}: PopupIconTooltipProps) {
  const classes = useStyles();

  useEffect(() => {
    if (onHover) {
      onHover();
    }
  }, [onHover]);

  return (
    <div className={classes.wrapper}>
      {isLoading ? (
        <div className={classes.loader}>
          <CircularProgress size={24} color='primary' />
        </div>
      ) : isError ? (
        <Text variant='normal'>{errorText}</Text>
      ) : !content ? (
        <Text variant='normal'>{emptyText}</Text>
      ) : (
        <Text variant='normal'>
          <div
            className={classes.bodyText}
            dangerouslySetInnerHTML={createMarkup(DOMPurify.sanitize(content))}
          />
        </Text>
      )}
    </div>
  );
}

function PopupIcon({
  to,
  icon,
  testId,
  isNoIcon,
  enterTouchDelay,
  ...tooltipProps
}: PopupIconProps) {
  const classes = useStyles();

  if (isNoIcon) return <NoIcon />;

  return (
    <STooltip
      title={<PopupIconooltip {...tooltipProps} />}
      enterTouchDelay={enterTouchDelay}>
      {to ? (
        <MaterialLink
          data-testid={testId}
          onClick={(e: SyntheticEvent<any>) => e.stopPropagation()}
          component={Link}
          className={classes.link}
          to={to}>
          <IconButton>{icon}</IconButton>
        </MaterialLink>
      ) : (
        <IconButton data-testid={testId}>{icon}</IconButton>
      )}
    </STooltip>
  );
}

export default PopupIcon;
